import {
  Circle,
  DirectionsRenderer,
  GoogleMap,
  Marker,
  Polygon,
  withGoogleMap,
  withScriptjs,
  InfoWindow,
} from "react-google-maps";
import React, { useEffect, useRef } from "react";
import DrawingManager from "react-google-maps/lib/components/drawing/DrawingManager";
import { compose, withProps, withStateHandlers, lifecycle } from "recompose";
import { MAP } from "react-google-maps/lib/constants";
import { CloseOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "reducers/rootReducer";
import { SET_SELECTED_RESPONDER, SET_SELECTED_ASSETS} from "reducers/reducer";

const getPaths = (polygon: any) => {
  var polygonBounds = polygon.getPath().getArray();
  var bounds = [];
  for (var i = 0; i < polygonBounds.length; i++) {
    var point = {
      lat: polygonBounds[i].lat(),
      lng: polygonBounds[i].lng(),
    };
    bounds.push(point);
  }
  bounds.push(bounds[0]);
  return bounds;
};

/*global google*/
const Map = compose<
  any,
  {
    setActive?: (show?: boolean) => void;
    markers?: any[];
    assetMarkers?: any[];
    currentMarkers?: any[];
    currentLocation?: any;
    active?: boolean;
    getPlaces?: (bounds: any) => void;
  }
>(
  withProps({
    googleMapURL:
      "https://maps.googleapis.com/maps/api/js?key=AIzaSyDNIyoSDV1wnJVW8wdHkiNG-eZ2bMcq0iY&v=3.exp&libraries=geometry,drawing,places",
    loadingElement: <div style={{ height: `100%` }} />,
    containerElement: <div style={{ height: `100%` }} />,
    mapElement: <div style={{ height: `100%` }} />,
  }),
  withStateHandlers(
    () => ({
      isOpen: false,
      activeMarker: null,
      directions: null,
      selectDirectionPin: null,
      drawingMode: null,
      activeInfo: null,
    }),
    {
      onToggleOpen:
        ({ isOpen }) =>
        (marker) => ({
          isOpen: !isOpen,
          activeMarker: marker,
        }),
      showDirection: () => (result) => ({
        directions: result,
      }),
      setDrawingMode: () => (mode) => ({
        drawingMode: mode,
      }),
      setSelectDirectionPin: () => (pin) => ({
        selectDirectionPin: pin,
      }),
      setActiveInfo: () => (active) => ({
        activeInfo: active,
      }),
    }
  ),
  withScriptjs,
  withGoogleMap
)(
  ({
    isOpen,
    active,
    setActive,
    markers,
    assetMarkers,
    currentMarkers,
    currentLocation,
    getPlaces,
    setActiveInfo,
    activeInfo,
  }) => {
    const mapRef = useRef(null);
    let timeout: NodeJS.Timeout;
    const { selectedResponders, selectedAssets } = useSelector(
      (state: RootState) => state.auth
    );
    const dispatch = useDispatch();

    useEffect(() => {
      if (selectedResponders && selectedResponders.length > 0) {
        console.log("update component");
      }
    }, [selectedResponders]);

    const onBoundsChanged = () => {
      if (mapRef.current) {
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(() => {
          let bounds = mapRef.current.getBounds();
          if (bounds) {
            let ne = bounds.getNorthEast();
            let sw = bounds.getSouthWest();
            getPlaces({
              center: mapRef.current.getCenter(),
              bounds: {
                minLat: sw.lat(),
                maxLat: ne.lat(),
                minLon: sw.lng(),
                maxLon: ne.lng(),
              },
            });
          }
        }, 500);
      }
    };

    const fitBounds = () => {
      const bounds = new google.maps.LatLngBounds();
      markers.forEach((marker: any) => {
        bounds.extend({ lat: marker.lat, lng: marker.lng });
      });
      assetMarkers.forEach((marker: any) => {
        bounds.extend({ lat: marker.lat, lng: marker.lng });
      });
      currentMarkers.forEach((marker: any) => {
        bounds.extend({ lat: marker.lat, lng: marker.lng });
      });
      if (bounds.isEmpty()) return;
      mapRef.current.fitBounds(bounds);
      // if (currentMarkers.length === 1 && markers.length === 0) {
      //   setTimeout(() => {
      //     mapRef.current.context[MAP].setZoom(15);
      //   }, 100);
      // }
    };

    // useEffect(() => {
    //   if (currentLocation && mapRef.current) {
    //     mapRef.current.context[MAP].setZoom(16);
    //   }
    // }, [currentLocation]);

    useEffect(() => {
      fitBounds();
    }, [markers, currentMarkers, assetMarkers]);

    return (
      <GoogleMap
        defaultZoom={14}
        options={{ maxZoom: 17 }}
        center={currentLocation}
        onZoomChanged={() => {
          onBoundsChanged();
        }}
        onCenterChanged={() => {
          onBoundsChanged();
        }}
        onTilesLoaded={() => {
          if (mapRef.current && currentLocation) {
            onBoundsChanged();
          }
        }}
        ref={mapRef}
      >
        {markers.map((marker: any, idx: number) => (
          <Marker
            key={idx}
            icon={
              marker.markerIcon
                ? marker.markerIcon
                : {
                    url: `/images/map-plain-icon-${
                      selectedResponders.findIndex((selected) => {
                        return selected.id === marker.id;
                      }) === -1
                        ? marker.color
                        : "green"
                    }.svg`,
                    scaledSize: new google.maps.Size(22, 34),
                  }
            }
            position={{ lat: marker.lat, lng: marker.lng }}
            onClick={() => {
              dispatch({ type: SET_SELECTED_RESPONDER, payload: marker });
              // on map click
            }}
            label={marker.title}
          ></Marker>
        ))}

        {assetMarkers.map((marker: any, idx: number) => (
          <Marker
            key={idx}
            icon={
              marker.markerIcon
                ? marker.markerIcon
                : {
                    url: `/images/map-plain-icon-${
                      selectedAssets.findIndex((selected) => {
                        return selected.id === marker.id;
                      }) === -1
                        ? marker.color
                        : "green"
                    }.svg`,
                    scaledSize: new google.maps.Size(22, 34),
                  }
            }
            position={{ lat: marker.lat, lng: marker.lng }}
            onClick={() => {
              dispatch({ type: SET_SELECTED_ASSETS, payload: marker });
              // on map click
            }}
            label={marker.title}
          ></Marker>
        ))}

        {currentMarkers.map((marker: any, idx: number) => (
          <Marker
            key={idx}
            icon={
              marker.markerIcon
                ? marker.markerIcon
                : {
                    url: `/images/map-plain-icon-${
                      selectedResponders.findIndex((selected) => {
                        return selected.id === marker.id;
                      }) === -1
                        ? marker.color
                        : "green"
                    }.svg`,
                    scaledSize: new google.maps.Size(22, 34),
                  }
            }
            // label={marker.title}
            position={{ lat: marker.lat, lng: marker.lng }}
            onClick={() => {
              setActiveInfo(idx);
            }}
          >
            {activeInfo === idx && (
              <InfoWindow
                onCloseClick={() => {
                  setActiveInfo(null);
                }}
              >
                <div>
                  <div>{marker.title}</div>
                </div>
              </InfoWindow>
            )}
          </Marker>
        ))}

        <Marker
          position={currentLocation}
          icon={{
            url: `/images/map-plain-icon-blue.svg`,
            scaledSize: new google.maps.Size(22, 34),
          }}
          onClick={() => {}}
        ></Marker>
      </GoogleMap>
    );
  }
);

export default Map;
